import { HubSpotProductName } from "src/hooks/useRevenueHero";
import {
  BRANDED_APP_STRIPE_PRICE_IDS,
  GROW_STRIPE_PRICE_IDS,
  STRIPE_PRICE_IDS,
  TRAIN_STRIPE_PRICE_IDS,
} from "../constants/subscriptionIds";
import brandedAppPreview from "../../../assets/img/photos/branded-app-preview.png";
import growAppPreview from "../../../assets/img/photos/grow-app-preview.png";
import trainAppPreview from "../../../assets/img/photos/train-app-preview.png";

interface SubscriptionDetails {
  priceIds: string[];
  title: string;
  description: string;
  prosperStackCancellationFlowId: string;
  marketingPageLink: string;
  revenueHeroProductTag: HubSpotProductName;
}

export interface CoreSubscriptionDetails extends SubscriptionDetails {
  subscriptionPricing: string;
  productDetails: string[];
}

interface AddOnSubscriptionDetails extends SubscriptionDetails {
  imgSrc: string;
}

export const stripeCoreSubscriptionInfo: CoreSubscriptionDetails[] = [
  {
    priceIds: [STRIPE_PRICE_IDS.coreFree],
    title: "Core Free",
    description: "Access a complete gym management platform for free.",
    prosperStackCancellationFlowId: process.env.PROSPER_STACK_CORE_FLOW_ID,
    marketingPageLink: "https://www.pushpress.com/features",
    revenueHeroProductTag: "Core",
    subscriptionPricing: "$0/month",
    productDetails: [
      "Automate billing and payments with recurring invoicing, ACH/credit card support, and failed payment handling.",
      "Streamline scheduling with class calendars, reservations, waitlists, and check-in options.",
      "Stay connected with members through built-in email, SMS, and real-time notifications.",
      "Enhance the member experience with a mobile app for scheduling, waivers, and account management.",
    ],
  },
  {
    priceIds: [STRIPE_PRICE_IDS.coreProMonthly],
    title: "Core Pro Monthly",
    description:
      "Unlock a suite of powerful features designed to elevate your gym management experience.",
    prosperStackCancellationFlowId: process.env.PROSPER_STACK_CORE_FLOW_ID,
    marketingPageLink: "https://www.pushpress.com/features",
    revenueHeroProductTag: "Core",
    subscriptionPricing: "$159/month",
    productDetails: [
      "Save on processing fees with up to $130 saved per $10K processed.",
      "Boost member engagement & retention with Committed Club and rank tracking.",
      "Seamlessly integrate tools like Slack, Mailchimp, Google Calendar, analytics tools, and more.",
      "Enjoy enhanced control through customized transaction fees, prorated plans, purchase limits, and priority booking.",
    ],
  },
  {
    priceIds: [STRIPE_PRICE_IDS.coreProAnnual],
    title: "Core Pro Annual",
    description:
      "Unlock a suite of powerful features designed to elevate your gym management experience.",
    prosperStackCancellationFlowId: process.env.PROSPER_STACK_CORE_FLOW_ID,
    marketingPageLink: "https://www.pushpress.com/features",
    revenueHeroProductTag: "Core",
    subscriptionPricing: "$1,599/year",
    productDetails: [
      "Save on processing fees with up to $130 saved per $10K processed.",
      "Boost member engagement & retention with Committed Club and rank tracking.",
      "Seamlessly integrate tools like Slack, Mailchimp, Google Calendar, analytics tools, and more.",
      "Enjoy enhanced control through customized transaction fees, prorated plans, purchase limits, and priority booking.",
    ],
  },
  {
    priceIds: [STRIPE_PRICE_IDS.coreAnnualDiscounted],
    title: "Core Pro Annual",
    description:
      "Unlock a suite of powerful features designed to elevate your gym management experience.",
    prosperStackCancellationFlowId: process.env.PROSPER_STACK_CORE_FLOW_ID,
    marketingPageLink: "https://www.pushpress.com/features",
    revenueHeroProductTag: "Core",
    subscriptionPricing: "$1,908/year",
    productDetails: [
      "Save on processing fees with up to $130 saved per $10K processed.",
      "Boost member engagement & retention with Committed Club and rank tracking.",
      "Seamlessly integrate tools like Slack, Mailchimp, Google Calendar, analytics tools, and more.",
      "Enjoy enhanced control through customized transaction fees, prorated plans, purchase limits, and priority booking.",
    ],
  },
  {
    priceIds: [STRIPE_PRICE_IDS.coreMaxMonthly],
    title: "Core Max Monthly",
    description:
      "Access the highest level of support and savings for your established fitness business.",
    prosperStackCancellationFlowId: process.env.PROSPER_STACK_CORE_FLOW_ID,
    marketingPageLink: "https://www.pushpress.com/features",
    revenueHeroProductTag: "Core",
    subscriptionPricing: "$229/month",
    productDetails: [
      "Save even further on processing fees with an additional .14% lower rate than Pro. That's nearly 2% saved for each transaction compared to Free.",
    ],
  },
  {
    priceIds: [STRIPE_PRICE_IDS.coreMaxAnnual],
    title: "Core Max Annual",
    description:
      "Access the highest level of support and savings for your established fitness business.",
    prosperStackCancellationFlowId: process.env.PROSPER_STACK_CORE_FLOW_ID,
    marketingPageLink: "https://www.pushpress.com/features",
    revenueHeroProductTag: "Core",
    subscriptionPricing: "$2,299/year",
    productDetails: [
      "Save even further on processing fees with an additional .14% lower rate than Pro. That's nearly 2% saved for each transaction compared to Free.",
    ],
  },
];

export const stripeAddOnSubscriptionInfo: AddOnSubscriptionDetails[] = [
  {
    priceIds: TRAIN_STRIPE_PRICE_IDS,
    title: "Train",
    description:
      "Design, deliver, and track workouts. Provide training platforms built for fitness professionals.",
    prosperStackCancellationFlowId: process.env.PROSPER_STACK_TRAIN_FLOW_ID,
    marketingPageLink: "https://www.pushpress.com/products/train",
    revenueHeroProductTag: "Train",
    imgSrc: trainAppPreview,
  },
  {
    priceIds: [STRIPE_PRICE_IDS.trainFree],
    title: "Train Lite",
    description:
      "No-cost solution for gyms that want to display digital programming on PushPress Screens and the PushPress Members App.",
    prosperStackCancellationFlowId: process.env.PROSPER_STACK_TRAIN_FLOW_ID,
    marketingPageLink:
      "https://help.pushpress.com/en/articles/10542307-train-lite-overview-faqs",
    revenueHeroProductTag: "Train",
    imgSrc: trainAppPreview,
  },
  {
    priceIds: GROW_STRIPE_PRICE_IDS,
    title: "Grow",
    description:
      "Generate leads with a mobile-ready website, then engage prospects effortlessly and retain members with an easy-to-use CRM designed to help fitness businesses thrive.",
    prosperStackCancellationFlowId: process.env.PROSPER_STACK_GROW_FLOW_ID,
    marketingPageLink: "https://www.pushpress.com/products/grow",
    revenueHeroProductTag: "Grow",
    imgSrc: growAppPreview,
  },
  {
    priceIds: BRANDED_APP_STRIPE_PRICE_IDS,
    title: "Branded App",
    description:
      "Turn the Members App into a fully customized app featuring your logo, personalized background, and placement on your members' phone screens.",
    prosperStackCancellationFlowId:
      process.env.PROSPER_STACK_BRANDED_APP_FLOW_ID,
    marketingPageLink: "https://www.pushpress.com/products/branded-member-app",
    revenueHeroProductTag: "Branded App",
    imgSrc: brandedAppPreview,
  },
];

export const allStripeSubscriptionInfo: SubscriptionDetails[] = [
  ...stripeCoreSubscriptionInfo,
  ...stripeAddOnSubscriptionInfo,
];

export function getSubscriptionInfo<
  T extends
    | AddOnSubscriptionDetails
    | CoreSubscriptionDetails
    | SubscriptionDetails,
>(subscriptionInfo: T[], priceId: string): T | undefined {
  return subscriptionInfo.find((subscription) =>
    subscription.priceIds.includes(priceId),
  );
}
